import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';
import { throwErrorMessage } from '../utils/api';

const VIEW_PREFERENCE_STORAGE_KEY = 'kanban.viewPreferenceByInbox';

const loadStoredPreferences = () => {
  try {
    const raw = window.localStorage.getItem(VIEW_PREFERENCE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const persistPreferences = preferences => {
  try {
    window.localStorage.setItem(
      VIEW_PREFERENCE_STORAGE_KEY,
      JSON.stringify(preferences)
    );
  } catch (e) {
    // ignore quota errors
  }
};

// Só envia pra API os filtros de fato preenchidos.
// `teamId` pode ser o id do time ou 'none' (conversas sem setor) — o backend
// entende os dois. As datas da reunião vão como AAAA-MM-DD, que é o que o
// <input type="date"> já produz e o que o backend aceita.
export const buildFilterParams = (filters = {}) => {
  const params = {};
  if (filters.q) params.q = filters.q;
  if (filters.label) params.label = filters.label;
  if (filters.assigneeId) params.assignee_id = filters.assigneeId;
  if (filters.priority) params.priority = filters.priority;
  if (filters.inboxId) params.inbox_id = filters.inboxId;
  if (filters.teamId) params.team_id = filters.teamId;
  if (filters.reuniaoDe) params.reuniao_de = filters.reuniaoDe;
  if (filters.reuniaoAte) params.reuniao_ate = filters.reuniaoAte;
  return params;
};

// Nome que o backend deu à planilha (Content-Disposition), com fallback — o
// servidor é quem sabe a data "de hoje" em São Paulo e o slug da coluna.
export const exportFileNameFrom = (disposition, fallback) => {
  const match = /filename="([^"]+)"/.exec(disposition || '');
  return match ? match[1] : fallback;
};

// Constrói o meta por coluna (total/page/hasMore) a partir dos totais do servidor
// e dos cards já carregados (1ª página de todas as colunas). Deriva das chaves do
// próprio column_totals — não depende de `columns` já ter chegado (evita race
// fetchColumns × fetchCards); coluna sem match fica de fora e o getter cobre com
// o default zerado.
const buildColumnMeta = (cards, columnTotals = {}) => {
  const loadedByColumn = cards.reduce((acc, card) => {
    acc[card.kanban_column_id] = (acc[card.kanban_column_id] || 0) + 1;
    return acc;
  }, {});

  const meta = {};
  Object.keys(columnTotals).forEach(key => {
    const columnId = Number(key);
    const total = Number(columnTotals[key]) || 0;
    const loaded = loadedByColumn[columnId] || 0;
    meta[columnId] = {
      total,
      page: 1,
      hasMore: loaded < total,
      isLoadingMore: false,
    };
  });
  return meta;
};

// Espelha no client o shift que o servidor faz na coluna destino
// (position >= alvo → +1), pra ordem local bater com a do banco sem refetch.
const shiftColumnCards = (
  $state,
  { columnId, fromPosition, exceptConversationId }
) => {
  $state.cards = $state.cards.map(card => {
    if (
      card.kanban_column_id === Number(columnId) &&
      card.position >= fromPosition &&
      card.conversation_id !== exceptConversationId
    ) {
      return { ...card, position: card.position + 1 };
    }
    return card;
  });
};

// Board agrupado por CLIENTE: o card na tela representa o contato, e o realtime
// pode chegar de OUTRA conversa do mesmo contato. Sem o id do contato não dá pra
// reconhecer o parentesco (cada conversa tem conversation_id próprio).
const contactIdOf = card => card?.contact?.id ?? null;

// Mesma regra de desempate do servidor (Kanban::FetchCardsService): vence a
// conversa de maior last_activity_at e, no empate, o maior conversation_id.
const isMoreRecentThan = (card, other) => {
  const a = Number(card?.last_activity_at ?? 0);
  const b = Number(other?.last_activity_at ?? 0);
  if (a !== b) return a > b;
  return (
    Number(card?.conversation_id ?? 0) >= Number(other?.conversation_id ?? 0)
  );
};

// Ajusta o total das colunas de origem/destino quando um card troca de coluna.
const adjustColumnTotals = ($state, { fromColumnId, toColumnId }) => {
  if (!fromColumnId || !toColumnId || fromColumnId === toColumnId) return {};
  const updates = {};
  [
    [fromColumnId, -1],
    [toColumnId, +1],
  ].forEach(([columnId, delta]) => {
    const meta = $state.columnMeta[Number(columnId)];
    if (meta) {
      updates[Number(columnId)] = {
        ...meta,
        total: Math.max(0, meta.total + delta),
      };
    }
  });
  return updates;
};

export const state = {
  columns: [],
  cards: [],
  // Modo do board, por conta: 'status' (colunas seguem o estado da conversa)
  // ou 'label' (cada coluna é uma etapa do funil identificada por etiqueta).
  mode: 'status',
  // O que um card representa: 'conversation' (padrão) ou 'contact' — um card por
  // cliente, na etapa da conversa mais recente dele.
  grouping: 'conversation',
  // Se ESTE usuário pode arrastar card. Vem pronto do backend, que já cruzou a
  // configuração da conta com o papel de quem pediu — a tela não decide isso.
  canDrag: true,
  filters: {
    q: '',
    label: null,
    assigneeId: null,
    priority: null,
    inboxId: null,
    // Setor: id do time, 'none' (sem setor) ou null (todos).
    teamId: null,
    // Período da reunião (custom attribute `reuniao_prevista_em`), AAAA-MM-DD.
    reuniaoDe: null,
    reuniaoAte: null,
  },
  columnMeta: {},
  sortByColumn: {},
  uiFlags: {
    isFetchingColumns: false,
    isFetchingCards: false,
    isCreatingColumn: false,
    isUpdatingColumn: false,
    isDeletingColumn: false,
    isMovingCard: false,
    isUpdatingSettings: false,
  },
  uiPreferenceByInbox: loadStoredPreferences(),
};

export const getters = {
  getColumns($state) {
    return [...$state.columns].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );
  },
  getColumn: $state => id => {
    return $state.columns.find(col => col.id === Number(id));
  },
  getCards($state) {
    return $state.cards;
  },
  getCardsByColumn: $state => columnId => {
    const cid = Number(columnId);
    const list = $state.cards.filter(card => card.kanban_column_id === cid);
    const sort = $state.sortByColumn[cid];
    // Sort de campo ativo: preserva a ordem recebida do servidor.
    if (sort && sort.by && sort.by !== 'position') return list;
    return list.sort(
      (a, b) =>
        (a.position ?? 0) - (b.position ?? 0) || (a.id ?? 0) - (b.id ?? 0)
    );
  },
  getColumnSort: $state => columnId =>
    $state.sortByColumn[Number(columnId)] || {
      by: 'position',
      order: 'asc',
      key: null,
    },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getKanbanMode($state) {
    return $state.mode;
  },
  isLabelMode($state) {
    return $state.mode === 'label';
  },
  canDragCards($state) {
    return $state.canDrag;
  },
  getKanbanGrouping($state) {
    return $state.grouping;
  },
  isContactGrouping($state) {
    return $state.grouping === 'contact';
  },
  getViewPreference: $state => inboxId => {
    const key = inboxId ? String(inboxId) : 'default';
    return $state.uiPreferenceByInbox[key] || 'list';
  },
  getFilters($state) {
    return $state.filters;
  },
  getColumnMeta: $state => columnId => {
    return (
      $state.columnMeta[Number(columnId)] || {
        total: 0,
        page: 1,
        hasMore: false,
        isLoadingMore: false,
      }
    );
  },
  getActiveFilterCount($state) {
    const { label, assigneeId, priority, teamId, reuniaoDe, reuniaoAte } =
      $state.filters;
    // O período conta como UM filtro, esteja preenchido de um lado ou dos dois.
    const period = reuniaoDe || reuniaoAte;
    return [label, assigneeId, priority, teamId, period].filter(
      value => value !== null && value !== undefined && value !== ''
    ).length;
  },
};

export const actions = {
  fetchSettings: async ({ commit }) => {
    try {
      const response = await KanbanAPI.getSettings();
      commit(types.SET_KANBAN_SETTINGS, response.data);
    } catch (error) {
      // Modo é só apresentação: sem resposta, o board segue no padrão 'status'.
    }
  },

  updateMode: async ({ commit }, mode) => {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdatingSettings: true });
    try {
      const response = await KanbanAPI.updateSettings({ kanban_mode: mode });
      commit(types.SET_KANBAN_SETTINGS, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdatingSettings: false });
    }
  },

  // Um card por conversa × um card por cliente. Muda a contagem de todas as
  // colunas, então o board precisa refazer o fetch depois — quem chama cuida disso.
  updateGrouping: async ({ commit }, grouping) => {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdatingSettings: true });
    try {
      const response = await KanbanAPI.updateSettings({
        kanban_grouping: grouping,
      });
      commit(types.SET_KANBAN_SETTINGS, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdatingSettings: false });
    }
  },

  fetchColumns: async ({ commit }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingColumns: true });
    try {
      const response = await KanbanAPI.getColumns();
      commit(types.SET_KANBAN_COLUMNS, response.data);
    } catch (error) {
      // Surface in UI via flag reset; do not throw to avoid breaking page mount
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingColumns: false });
    }
  },

  createColumn: async ({ commit }, data) => {
    commit(types.SET_KANBAN_UI_FLAG, { isCreatingColumn: true });
    try {
      const response = await KanbanAPI.createColumn(data);
      commit(types.ADD_KANBAN_COLUMN, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isCreatingColumn: false });
    }
  },

  updateColumn: async ({ commit }, { id, data }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isUpdatingColumn: true });
    try {
      const response = await KanbanAPI.updateColumn(id, data);
      commit(types.EDIT_KANBAN_COLUMN, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isUpdatingColumn: false });
    }
  },

  deleteColumn: async ({ commit }, { id, moveCardsTo }) => {
    commit(types.SET_KANBAN_UI_FLAG, { isDeletingColumn: true });
    try {
      const params = moveCardsTo ? { move_cards_to: moveCardsTo } : {};
      await KanbanAPI.deleteColumn(id, params);
      commit(types.DELETE_KANBAN_COLUMN, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isDeletingColumn: false });
    }
  },

  reorderColumns: async ({ commit }, items) => {
    try {
      const response = await KanbanAPI.reorderColumns(items);
      commit(types.SET_KANBAN_COLUMNS, response.data);
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  fetchCards: async ({ commit, state: $state }, params = {}) => {
    commit(types.SET_KANBAN_UI_FLAG, { isFetchingCards: true });
    try {
      const requestParams = {
        ...buildFilterParams($state.filters),
        ...params,
      };
      const response = await KanbanAPI.getCards(requestParams);
      const cards = response.data?.cards || [];
      const columnTotals = response.data?.meta?.column_totals || {};
      commit(types.SET_KANBAN_CARDS, cards);
      commit(
        types.RESET_KANBAN_COLUMN_META,
        buildColumnMeta(cards, columnTotals)
      );
    } catch (error) {
      // ignore for resiliency
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isFetchingCards: false });
    }
  },

  // Merge de filtros + refetch da página 1 de todas as colunas.
  setFilters: async ({ commit, dispatch }, partial = {}) => {
    commit(types.SET_KANBAN_FILTERS, partial);
    await dispatch('fetchCards');
  },

  // Planilha de UMA coluna com os filtros e a ordenação que a tela mostra agora.
  // Devolve o arquivo para quem chamou disparar o download (DOM é da tela).
  exportColumn: async ({ state: $state }, columnId) => {
    const numericColumnId = Number(columnId);
    const sort = $state.sortByColumn[numericColumnId] || {};
    const params = {
      ...buildFilterParams($state.filters),
      column_id: numericColumnId,
      ...(sort.by && sort.by !== 'position'
        ? {
            sort_by: sort.by,
            sort_order: sort.order,
            ...(sort.key ? { sort_key: sort.key } : {}),
          }
        : {}),
    };
    const response = await KanbanAPI.exportCards(params);
    const headers = response.headers || {};
    return {
      blob: response.data,
      fileName: exportFileNameFrom(
        headers['content-disposition'],
        `kanban-${numericColumnId}.csv`
      ),
      truncated: headers['x-kanban-export-truncated'] === 'true',
    };
  },

  // Define o sort de UMA coluna + refetch da página 1 dela, já ordenada pelo servidor.
  sortColumn: async (
    { commit, state: $state },
    { columnId, by, order, key = null }
  ) => {
    const numericColumnId = Number(columnId);
    commit(types.SET_KANBAN_COLUMN_SORT, {
      columnId: numericColumnId,
      sort: { by, order, key },
    });
    try {
      const params = {
        ...buildFilterParams($state.filters),
        column_id: numericColumnId,
        page: 1,
        sort_by: by,
        sort_order: order,
        ...(key ? { sort_key: key } : {}),
      };
      const response = await KanbanAPI.getCards(params);
      const cards = response.data?.cards || [];
      const columnTotals = response.data?.meta?.column_totals || {};
      commit(types.SET_KANBAN_COLUMN_CARDS, {
        columnId: numericColumnId,
        cards,
      });
      const total = Number(
        columnTotals[numericColumnId] ??
          columnTotals[String(numericColumnId)] ??
          cards.length
      );
      commit(types.SET_KANBAN_COLUMN_META, {
        [numericColumnId]: {
          total,
          page: 1,
          hasMore: cards.length < total,
          isLoadingMore: false,
        },
      });
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  // "Carregar mais" de uma coluna específica (infinite scroll paginado).
  loadMoreCards: async ({ commit, state: $state }, columnId) => {
    const numericColumnId = Number(columnId);
    const meta = $state.columnMeta[numericColumnId];
    if (!meta || meta.isLoadingMore || !meta.hasMore) return;

    commit(types.SET_KANBAN_COLUMN_META, {
      [numericColumnId]: { ...meta, isLoadingMore: true },
    });

    try {
      const sort = $state.sortByColumn[numericColumnId] || {};
      const params = {
        ...buildFilterParams($state.filters),
        column_id: numericColumnId,
        page: meta.page + 1,
        ...(sort.by && sort.by !== 'position'
          ? {
              sort_by: sort.by,
              sort_order: sort.order,
              ...(sort.key ? { sort_key: sort.key } : {}),
            }
          : {}),
      };
      const response = await KanbanAPI.getCards(params);
      const cards = response.data?.cards || [];
      const columnTotals = response.data?.meta?.column_totals || {};

      commit(types.APPEND_KANBAN_CARDS, cards);

      const total = Number(
        columnTotals[numericColumnId] ??
          columnTotals[String(numericColumnId)] ??
          meta.total
      );
      const loaded = $state.cards.filter(
        card => card.kanban_column_id === numericColumnId
      ).length;

      commit(types.SET_KANBAN_COLUMN_META, {
        [numericColumnId]: {
          total,
          page: meta.page + 1,
          hasMore: loaded < total,
          isLoadingMore: false,
        },
      });
    } catch (error) {
      commit(types.SET_KANBAN_COLUMN_META, {
        [numericColumnId]: { ...meta, isLoadingMore: false },
      });
      throwErrorMessage(error);
    }
  },

  // Realtime: aplica no board um card movido/criado em outra sessão (ActionCable).
  updateCardRealtime: ({ commit, state: $state }, card) => {
    if (!card || !card.conversation_id) return;

    let existing = $state.cards.find(
      c => c.conversation_id === card.conversation_id
    );

    // Agrupado por CLIENTE: o board mostra um card por contato, e este evento
    // pode ser de outra conversa do MESMO contato. Ele não entra ao lado — ou
    // substitui o que está lá (se for a conversa mais recente) ou é ignorado.
    // Sem isto o realtime devolveria à tela a duplicata que o agrupamento tirou.
    const twin =
      !existing && $state.grouping === 'contact' && contactIdOf(card)
        ? $state.cards.find(c => contactIdOf(c) === contactIdOf(card))
        : null;
    if (twin) {
      if (!isMoreRecentThan(card, twin)) return;
      existing = twin;
    }

    // Broadcast do nosso próprio move (já aplicado + reconciliado): no-op.
    if (
      existing &&
      !twin &&
      existing.kanban_column_id === card.kanban_column_id &&
      existing.position === card.position
    ) {
      return;
    }

    // Espelha o shift do servidor + ajusta os totais das colunas envolvidas.
    commit(types.SHIFT_KANBAN_CARD_POSITIONS, {
      columnId: card.kanban_column_id,
      fromPosition: card.position,
      exceptConversationId: card.conversation_id,
    });
    const totalUpdates = adjustColumnTotals($state, {
      fromColumnId: existing?.kanban_column_id,
      toColumnId: card.kanban_column_id,
    });
    if (Object.keys(totalUpdates).length) {
      commit(types.SET_KANBAN_COLUMN_META, totalUpdates);
    }
    // O card do cliente passa a ser o de outra conversa: tira o antigo antes de
    // pôr o novo, senão o mesmo contato fica em dois lugares.
    if (twin) commit(types.REMOVE_KANBAN_CARD, twin.conversation_id);
    commit(types.UPDATE_KANBAN_CARD, card);
  },

  moveCard: async ({ commit, dispatch, state: $state }, payload) => {
    const { conversationId, toColumnId, afterCardId, beforeCardId } = payload;
    const card = $state.cards.find(
      c => c.conversation_id === Number(conversationId)
    );

    // Mesma matemática do servidor (after+1 > before > 0): com o shift local
    // espelhado, a posição otimista já é a final — o reconcile vira formalidade.
    const afterCard = afterCardId
      ? $state.cards.find(c => c.id === afterCardId)
      : null;
    const beforeCard = beforeCardId
      ? $state.cards.find(c => c.id === beforeCardId)
      : null;
    const targetPosition = (() => {
      if (afterCard) return afterCard.position + 1;
      if (beforeCard) return beforeCard.position;
      return 0;
    })();

    // Optimistic: abre espaço na coluna destino e posiciona o card.
    if (card) {
      commit(types.SHIFT_KANBAN_CARD_POSITIONS, {
        columnId: toColumnId,
        fromPosition: targetPosition,
        exceptConversationId: card.conversation_id,
      });
      const totalUpdates = adjustColumnTotals($state, {
        fromColumnId: card.kanban_column_id,
        toColumnId,
      });
      if (Object.keys(totalUpdates).length) {
        commit(types.SET_KANBAN_COLUMN_META, totalUpdates);
      }
      commit(types.UPDATE_KANBAN_CARD, {
        conversation_id: card.conversation_id,
        kanban_column_id: toColumnId,
        position: targetPosition,
      });
    }

    commit(types.SET_KANBAN_UI_FLAG, { isMovingCard: true });
    try {
      const response = await KanbanAPI.moveCard(conversationId, {
        to_column_id: toColumnId,
        after_card_id: afterCardId ?? null,
        before_card_id: beforeCardId ?? null,
      });
      // Reconcile with server response if returned
      if (response?.data) {
        commit(types.UPDATE_KANBAN_CARD, response.data);
      }
      return response?.data;
    } catch (error) {
      // Reverter shift+totais granularmente é frágil; um refetch garante consistência.
      dispatch('fetchCards');
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_KANBAN_UI_FLAG, { isMovingCard: false });
    }
  },

  setViewPreference: ({ commit, state: $state }, { inboxId, view }) => {
    const key = inboxId ? String(inboxId) : 'default';
    const next = { ...$state.uiPreferenceByInbox, [key]: view };
    persistPreferences(next);
    commit(types.SET_KANBAN_VIEW_PREFERENCE, next);
  },
};

export const mutations = {
  [types.SET_KANBAN_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_KANBAN_COLUMNS]($state, data) {
    $state.columns = data || [];
  },
  [types.SET_KANBAN_SETTINGS]($state, data) {
    $state.mode = data?.kanban_mode || 'status';
    $state.grouping = data?.kanban_grouping || 'conversation';
    // `undefined` (resposta antiga, sem o campo) mantém o arraste liberado, que
    // é como o board sempre funcionou.
    $state.canDrag = data?.can_drag !== false;
  },
  [types.ADD_KANBAN_COLUMN]($state, data) {
    const index = $state.columns.findIndex(col => col.id === data.id);
    if (index === -1) {
      $state.columns.push(data);
    } else {
      $state.columns.splice(index, 1, data);
    }
  },
  [types.EDIT_KANBAN_COLUMN]($state, data) {
    const index = $state.columns.findIndex(col => col.id === data.id);
    if (index !== -1) {
      $state.columns.splice(index, 1, data);
    } else {
      $state.columns.push(data);
    }
  },
  [types.DELETE_KANBAN_COLUMN]($state, id) {
    $state.columns = $state.columns.filter(col => col.id !== Number(id));
  },
  [types.SET_KANBAN_CARDS]($state, data) {
    // API retorna array plano; tolera shapes inesperados sem derrubar o board.
    $state.cards = Array.isArray(data) ? data : data?.payload || [];
    // Refetch cheio (filtro/inbox/load inicial) volta as colunas ao sort padrão
    // (window function = posição). Zera o sort por coluna pra o dropdown não
    // mentir a ordem ativa enquanto os cards vêm em ordem de posição.
    $state.sortByColumn = {};
  },
  [types.APPEND_KANBAN_CARDS]($state, data) {
    const incoming = Array.isArray(data) ? data : [];
    const existingIds = new Set($state.cards.map(c => c.conversation_id));
    const newCards = incoming.filter(
      card => !existingIds.has(card.conversation_id)
    );
    $state.cards = [...$state.cards, ...newCards];
  },
  [types.UPDATE_KANBAN_CARD]($state, data) {
    const index = $state.cards.findIndex(
      c => c.conversation_id === data.conversation_id
    );
    if (index !== -1) {
      $state.cards.splice(index, 1, { ...$state.cards[index], ...data });
    } else {
      $state.cards.push(data);
    }
  },
  [types.REMOVE_KANBAN_CARD]($state, conversationId) {
    $state.cards = $state.cards.filter(
      card => card.conversation_id !== conversationId
    );
  },
  [types.SET_KANBAN_VIEW_PREFERENCE]($state, data) {
    $state.uiPreferenceByInbox = data;
  },
  [types.SET_KANBAN_FILTERS]($state, partial) {
    $state.filters = { ...$state.filters, ...partial };
  },
  [types.SET_KANBAN_COLUMN_META]($state, data) {
    $state.columnMeta = { ...$state.columnMeta, ...data };
  },
  [types.RESET_KANBAN_COLUMN_META]($state, data) {
    $state.columnMeta = data || {};
  },
  [types.SHIFT_KANBAN_CARD_POSITIONS]($state, payload) {
    shiftColumnCards($state, payload);
  },
  [types.SET_KANBAN_COLUMN_SORT]($state, { columnId, sort }) {
    $state.sortByColumn = { ...$state.sortByColumn, [columnId]: sort };
  },
  // Substitui os cards de UMA coluna preservando a ordem recebida do servidor.
  [types.SET_KANBAN_COLUMN_CARDS]($state, { columnId, cards }) {
    const others = $state.cards.filter(
      c => c.kanban_column_id !== Number(columnId)
    );
    $state.cards = [...others, ...cards];
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
